/**
 * AI CONTEXT
 * Purpose: prepares curriculum table data with semester filter + search + sort + readiness gating.
 * Data flow: filtered curriculum subscription -> Curriculum mapper -> sort -> optimized search.
 * Uses: user settings for persisted filter/search/sort config.
 * Used by: CurriculumsTable view.
 * Refactor: replace timeout gating with standardized fulfillment helper pattern.
 */
import { useEffect, useMemo, useState } from "react"

import { curriculumAtoms } from "#atoms"
import { Curriculum } from "#classes/Curriculum"
import {
  CURRICULUM_SEARCHABLE_FIELDS,
  CURRICULUM_SORTABLE_FIELDS,
  getCurriculumSortValue,
} from "#constants/curriculumOptions"
import { USER_SETTINGS } from "#constants/globalConstants"
import { useUserContext } from "#contexts/UserContextProvider"
import {
  CurriculumConstructor,
  CurriculumForTable,
} from "#customTypes/composedTypes/curriculumTypes"
import {
  COMPARATORS,
  DOC_TYPES,
  FirebaseQueryConfig,
} from "#customTypes/composedTypes/firebaseTypes"
import { SETTINGS_KEYS } from "#customTypes/globalTypes"
import { useIsAllFulfilled } from "#hooks/jotai/useCascadingData"
import { useFilteredDocs } from "#hooks/jotai/useSubscription"
import useOptimizedSearch from "#hooks/newHooks/useOptimizedSearch"

import { useTableSort } from "./useTableSort"

const useCurriculumTableData = () => {
  const { getUserSetting } = useUserContext()
  const filterSemester = getUserSetting<string>(
    USER_SETTINGS.CURRICULUMS_TABLE_SEMESTER
  )
  const resultsCount = getUserSetting<number>(
    USER_SETTINGS.CURRICULUMS_TABLE_RESULTS_COUNT
  )
  const defaultSearchTerm = getUserSetting<string>(
    USER_SETTINGS.CURRICULUMS_TABLE_SEARCH_TERM
  )

  const { sortField, sortOrder, sortRows, onSortChange } =
    useTableSort<CurriculumForTable>({
      tableKey: SETTINGS_KEYS.CURRICULUMS,
      sortableFields: CURRICULUM_SORTABLE_FIELDS,
      getSortValue: getCurriculumSortValue,
    })

  const query: FirebaseQueryConfig = useMemo(() => {
    if (filterSemester === "All") return {}

    return {
      queries: {
        semester: {
          comparator: COMPARATORS.EQUALS,
          value: filterSemester,
        },
      },
    }
  }, [filterSemester])

  const curriculumsFromDb = useFilteredDocs<CurriculumConstructor>(
    curriculumAtoms,
    DOC_TYPES.CURRICULUMS,
    query
  )

  // Show the table even if data never fully loads (5s safety timeout).
  const [timeoutReached, setTimeoutReached] = useState(false)
  const [hasShown, setHasShown] = useState(false)

  useEffect(() => {
    const timer = setTimeout(() => setTimeoutReached(true), 5000)
    return () => clearTimeout(timer)
  }, [])

  const allFulfilled = useIsAllFulfilled([curriculumsFromDb.length > 0])

  // Once shown, keep showing (don't flicker back to loading).
  useEffect(() => {
    if (allFulfilled || timeoutReached) setHasShown(true)
  }, [allFulfilled, timeoutReached])

  const baseRows = useMemo(() => {
    if (!curriculumsFromDb.length || !hasShown) return []

    return curriculumsFromDb.map((curriculum) => ({
      ...new Curriculum(curriculum).getCurriculumForTable(),
      lastUpdatedAt: curriculum.lastUpdatedAt,
    }))
  }, [curriculumsFromDb, hasShown])

  const sortedRows = useMemo(() => sortRows(baseRows), [sortRows, baseRows])

  const { filteredItems: filteredCurriculums, onSearch } =
    useOptimizedSearch<CurriculumForTable>(
      sortedRows,
      CURRICULUM_SEARCHABLE_FIELDS,
      resultsCount,
      defaultSearchTerm
    )

  const curriculumsForTable = useMemo(
    () => sortRows(filteredCurriculums),
    [sortRows, filteredCurriculums]
  )

  return {
    curriculumsForTable,
    onSearch,
    isReady: allFulfilled,
    errors: null,
    sortField,
    sortOrder,
    onSortChange,
  }
}

export default useCurriculumTableData
